import React, { lazy, Suspense, useState, useEffect, Fragment } from 'react'
import { withRouter } from 'react-router-dom'
import { Alert, Layout, Spin, Row, Button } from 'antd'
import { useDispatch } from 'react-redux'
import { appSelector } from '../helpers/appSelector'
import { AppDispatch } from '../helpers/appDispatch'
import {
  viewTransactionsOverviewRequest,
  exportViewTransactionsOverviewRequest,
  downloadReceiptRequest
} from '../store/transactions'
import { getMerchantsRequest } from '../store/reports'
import { isEmpty } from '../helpers/isEmpty'
import { TransactionHistory, MerchantData } from '../interfaces'
import { useTranslation } from 'react-i18next'
import moment from 'moment'

const { Content } = Layout

const TransactionFilters = lazy(
  () => import('../components/transactions/TransactionFilters')
)
const TransactionSummaryCards = lazy(
  () => import('../components/transactions/TransactionSummaryCards')
)
const TransactionTable = lazy(
  () => import('../components/transactions/TransactionTable')
)
const TransactionDetail = lazy(
  () => import('../components/transactions/TransactionDetail')
)
const CurrencyFilter = lazy(
  () => import('../components/transactions/CurrencyFilter')
)
const EmptyBox = lazy(() => import('../components/transactions/EmptyBox'))

const Transactions = () => {
  const dispatch: AppDispatch = useDispatch()
  const transaction = appSelector((state) => state.transaction)
  const reports = appSelector((state) => state.reports)
  const { t } = useTranslation()
  const [channelSearch, setChannelSearch] = useState('')
  const [searchValue, setSearchValue] = useState('')
  const [statusSearch, setStatusSearch] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [exportType, setExportType] = useState('')
  const [trx, setTrx] = useState<TransactionHistory | null>(null)
  const [switchView, setSwitchView] = useState(false)
  const [currency, setCurrency] = useState('USD')
  const [merchant, setMerchant] = useState<MerchantData | null>(null)
  const [pageSize, setPageSize] = useState(10)
  const [skip, setSkip] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const {
    transactions: trxReport,
    loading,
    isExporting,
    isRequestingDownload: isDownlaoding,
    transactionError,
    transactionExportError
  } = transaction
  const { merchants } = reports
  const trans = trxReport?.records || trxReport?.Records || trxReport?.data || []
  const trxSummary = trxReport?.summary || trxReport?.Summary
  const total =
    trxSummary?.recordsTotal ??
    trxSummary?.RecordsTotal ??
    trxReport?.total?.value ??
    trans.length

  const getErrorMessage = (err: any) => {
    if (typeof err === 'string') return err
    return err?.detail || err?.message || err?.title || JSON.stringify(err)
  }

  const getSearchParams = () => ({
    currency,
    pageSize,
    skip,
    periodFrom: fromDate,
    periodTo: toDate,
    merchant: merchant?.name || '',
    status: statusSearch,
    channel: channelSearch,
    searchValue: searchValue,
    exportType: exportType,
    fixedPeriod: 'overall'
  })

  useEffect(() => {
    // fetch transaction history
    // dispatch(getTransactionsRequest(params))
    const { merchants } = reports
    if (isEmpty(merchants)) {
      dispatch(getMerchantsRequest())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onClickRow = (transactionID: number) => {
    if (!isEmpty(trans)) {
      const trx = trans.find(
        (t) => t.transactionId === transactionID
      )
      if (trx !== undefined) {
        setTrx(trx)
        setSwitchView(true)
      }
    }
  }

  const onCloseScreen = () => {
    setSwitchView(false)
    setTrx(null)
  }

  const reloadTransaction = () => {
    const searchParams = getSearchParams()
    dispatch(viewTransactionsOverviewRequest(searchParams))
  }

  
  const onDownloadReceiptClick = (transactionId: number): void => {
    dispatch(downloadReceiptRequest(transactionId))
  }

  const onSelectCurrency = (value: string) => {
    setCurrency(value)
    setSkip(0)
    setCurrentPage(1)
    
    const searchParams = {
      currency: value,
      pageSize,
      skip: 0,
      periodFrom: fromDate,
      periodTo: toDate,
      status: '',
      channel: '',
      searchValue: '',
      merchant: '',
      exportType: '',
      fixedPeriod: 'overall'
    }
    
    dispatch(viewTransactionsOverviewRequest(searchParams))
  }

  const onLoadMore = (page: number, size: number) => {
    const newSkip = page - 1
    setPageSize(size)
    setSkip(newSkip)
    setCurrentPage(page)
    
    const searchParams = getSearchParams()
    searchParams.skip = newSkip
    searchParams.pageSize = size
    
    dispatch(viewTransactionsOverviewRequest(searchParams))
  }

  const onExportClick = (type: string) => {
    setExportType(type)
    const searchParams = getSearchParams()
    searchParams.exportType = type
    dispatch(exportViewTransactionsOverviewRequest(searchParams))
  }

  const onSearch = (values: any) => {
    setIsSearching(true)
    setSwitchView(false)
    setTrx(null)
    setSkip(0)
    setCurrentPage(1)
    
    const { status, channel, periodFrom, periodTo, query, merchant: merchantId } = values
    let m: MerchantData | undefined = undefined
    let pFrom: string = '',
      pTo: string = ''

    setChannelSearch(channel !== undefined ? channel : '')
    setStatusSearch(status !== undefined ? status : '')
    setSearchValue(query !== undefined ? query : '')
    
    if (periodFrom !== undefined && periodTo !== undefined) {
      pFrom = moment(periodFrom).format('MM/DD/YYYY')
      pTo = moment(periodTo).format('MM/DD/YYYY')
      setFromDate(pFrom)
      setToDate(pTo)
    }
    
    if (merchantId !== undefined) {
      m = merchants.find((m) => m.merchantId === merchantId)
      setMerchant(m !== undefined ? m : null)
    } else {
      setMerchant(null)
    }

    const searchParams = {
      currency,
      pageSize,
      skip: 0,
      periodFrom: pFrom,
      periodTo: pTo,
      merchant: m?.name || '',
      status: status || '',
      channel: channel || '',
      searchValue: query || '',
      exportType: '',
      fixedPeriod: 'overall'
    }

    dispatch(viewTransactionsOverviewRequest(searchParams))
  }

  const onReset = (form: any) => {
    form.resetFields()
    setSwitchView(false)
    setTrx(null)
    setSkip(0)
    setCurrentPage(1)
    setMerchant(null)
    setFromDate('')
    setToDate('')
    setStatusSearch('')
    setChannelSearch('')
    setSearchValue('')
    setIsSearching(false)
    
    const resetParams = {
      currency,
      pageSize: 10,
      skip: 0,
      periodFrom: '',
      periodTo: '',
      merchant: '',
      status: '',
      channel: '',
      searchValue: '',
      exportType: '',
      fixedPeriod: 'overall'
    }

    dispatch(viewTransactionsOverviewRequest(resetParams))
  }

  return (
    <div className="padding-box">
      <Content className="site-layout-background site-box">
        <Suspense
          fallback={
            <Row
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center'
              }}
            >
              <div style={{ marginTop: '200px' }}>
                <Spin />
              </div>
            </Row>
          }
        >
          <Fragment>
            <TransactionFilters
              merchants={merchants}
              onSearch={onSearch}
              onReset={onReset}
              translate={t}
            />
            {isSearching ? (
              <Fragment>
                {!switchView ? (
                  <Fragment>
                    {transactionError && (
                      <Alert
                        message="Failed to fetch data"
                        description={getErrorMessage(transactionError)}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    )}
                    {transactionExportError && (
                      <Alert
                        message="Failed to export data"
                        description={getErrorMessage(transactionExportError)}
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                    )}
                    <TransactionSummaryCards
                      trxReports={trxReport}
                      loading={loading}
                      translate={t}
                    />

                    <CurrencyFilter
                      onSelectCurrency={onSelectCurrency}
                      translate={t}
                    />
                    <div className="margin-top">
                      <Row style={{ position: 'relative' }}>
                        <h4 className="transaction-chart-text">
                          {t('general.transactionsChart')}
                        </h4>
                        <div className="utility-buttons">
                          {!isEmpty(trans) ? (
                            <>
                              <Button
                                type="primary"
                                className="export-buttons"
                                onClick={() => onExportClick('EXCEL')}
                                loading={isExporting && exportType === 'EXCEL'}
                              >
                                {t('general.export-excel')}
                              </Button>
                              {/* <Button
                          type="primary"
                          className="export-buttons"
                          onClick={() => onExportClick('PDF')}
                          loading={isExporting && exportType === 'PDF'}
                        >
                          {t('general.export-pdf')}
                        </Button> */}
                            </>
                          ) : null}
                          <Button
                            type="primary"
                            className="export-buttons reload"
                            onClick={() => reloadTransaction()}
                          >
                            {t('general.refresh')}
                          </Button>
                        </div>
                      </Row>

                      <TransactionTable
                        transactionHistory={trans}
                        onClickRow={onClickRow}
                        currency={currency}
                        loading={loading}
                        onLoadMore={onLoadMore}
                        total={total}
                        translate={t}
                        currentPage={currentPage}
                      />
                    </div>
                  </Fragment>
                ) : (
                  <TransactionDetail
                    onCloseScreen={onCloseScreen}
                    transaction={trx!}
                    isDownloading={isDownlaoding}
                    onDownloadReceiptClick={onDownloadReceiptClick}
                    translate={t}
                  />
                )}
              </Fragment>
            ) : (
              <EmptyBox header={t('general.filterToGetData')} description="" />
            )}
          </Fragment>
        </Suspense>
      </Content>
    </div>
  )
}

export default withRouter(Transactions)
